import { createAsyncThunk } from '@reduxjs/toolkit';
import {
	ReportConfig,
	UnifiedReportingResponse,
} from 'src/types/reportingTypes';
import { DateTime } from 'luxon';
import mixpanel from 'mixpanel-browser';
import FEATURES from 'src/featureMap';

const AUTH_SESSION_KEY = 'authed';

function getAccountName(): string {
	try {
		const authed = sessionStorage.getItem(AUTH_SESSION_KEY);
		if (authed) {
			const parsed = JSON.parse(authed);
			return parsed?.accountDetails?.account?.name ?? '';
		}
	} catch {
		// ignore parse errors
	}
	return '';
}

function trackReportsFetch({
	accountId,
	config,
	page,
	size,
	startDate,
	endDate,
	timeTaken,
	statusCode,
}: {
	accountId: string;
	config: ReportConfig;
	page: number;
	size: number;
	startDate: string | null;
	endDate: string | null;
	timeTaken: number;
	statusCode: number;
}) {
	const action = FEATURES.reportsFetch;
	const trackingData = {
		apiType: 'reports',
		params: [
			`page:${page}`,
			`size:${size}`,
			config.groupBy?.length > 0
				? `groupBy:${config.groupBy.join(',')}`
				: '',
			config.sortBy?.length > 0
				? `sortBy:${config.sortBy.join(',')}`
				: '',
			startDate ? `startDate:${startDate}` : '',
			endDate ? `endDate:${endDate}` : '',
			config.searchQuery ? `searchQuery:${config.searchQuery}` : '',
			config.filters?.length > 0
				? `filters:${config.filters
						.map((f) => `${f.fieldName}=${f.value}`)
						.join(';')}`
				: '',
		]
			.filter(Boolean)
			.join('|'),
		timeTakenInMs: timeTaken,
		statusCode,
		accountId,
		accountName: getAccountName(),
	};
	mixpanel.track(`DI ${action.id}`, trackingData);
}

export const fetchReportData = createAsyncThunk<
	UnifiedReportingResponse,
	FetchReportDataPayload,
	{ rejectValue: string }
>(
	'reporting/fetchReportData',
	async ({ api, id, config, page, size }, { rejectWithValue }) => {
		const zoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
		const { absoluteDateRangeStart, absoluteDateRangeEnd } = config;

		const formattedStartDate = absoluteDateRangeStart
			? DateTime.fromISO(absoluteDateRangeStart, {
					setZone: true,
			  }).toFormat("yyyy-MM-dd'T'HH:mm:ss.SSS")
			: null;

		const formattedEndDate = absoluteDateRangeEnd
			? DateTime.fromISO(absoluteDateRangeEnd, {
					setZone: true,
			  }).toFormat("yyyy-MM-dd'T'HH:mm:ss.SSS")
			: null;

		const updatedChartConfig = {
			...config,
			absoluteDateRangeStart: formattedStartDate,
			absoluteDateRangeEnd: formattedEndDate,
			groupBy: config.groupBy || [],
			sortBy: config.sortBy || [],
			filters: config.filters || [],
			hiddenColumns: config.hiddenColumns || [],
			topDisplayCount: config.topDisplayCount || 5,
		};

		const startTime = performance.now();

		try {
			const result = await api.getUnifiedReportingData(
				id,
				updatedChartConfig,
				page,
				size,
				zoneId,
			);

			trackReportsFetch({
				accountId: id,
				config,
				page,
				size,
				startDate: formattedStartDate,
				endDate: formattedEndDate,
				timeTaken: Math.round(performance.now() - startTime),
				statusCode: 200,
			});

			return result;
		} catch (error) {
			// Don't track aborted requests
			if (error instanceof DOMException && error.name === 'AbortError') {
				throw error;
			}

			const statusCode =
				error &&
				typeof error === 'object' &&
				'response' in error &&
				(error as any).response?.status
					? (error as any).response.status
					: 0;

			trackReportsFetch({
				accountId: id,
				config,
				page,
				size,
				startDate: formattedStartDate,
				endDate: formattedEndDate,
				timeTaken: Math.round(performance.now() - startTime),
				statusCode,
			});

			const message =
				error instanceof Error
					? error.message
					: 'An unexpected error occurred';
			return rejectWithValue(message);
		}
	},
);

type FetchReportDataPayload = {
	api: {
		getUnifiedReportingData: (
			id: string,
			payload: ReportConfig,
			page: number,
			size: number,
			zoneId: string,
		) => Promise<UnifiedReportingResponse>;
	};
	id: string;
	config: ReportConfig;
	page: number;
	size: number;
};
